import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FilterBarColumnConfig, FilterBarFilter, FilterBarItem } from './filter-bar.models';

/**
 * Generic filter bar component that can be used across different features.
 * Handles rendering of search field, dynamic filters, column management, and URL state persistence.
 *
 * @template TFormValue - Type of the form value
 * @template TQueryKey - Type of query parameter keys
 * @template TColumn - Type of column identifiers
 */
@Component({
  selector: 'app-filter-bar',
  templateUrl: './filter-bar.component.html',
  styleUrls: ['./filter-bar.component.scss'],
  standalone: false,
})
export class FilterBarComponent<
  TFormValue extends Record<string, any> = any,
  TQueryKey extends string = string,
  TColumn extends string | number = string,
> implements OnInit
{
  /** Configuration for available filters */
  @Input() availableFilters: Array<FilterBarItem<TQueryKey, TFormValue>> = [];

  /** Configuration for column management (optional) */
  @Input() columnConfig?: FilterBarColumnConfig<TColumn>;

  /** Form group containing all filter controls */
  @Input() form: FormGroup;

  /** Placeholder text for search field */
  @Input() searchPlaceholder = 'Type to search';

  /** Label for search field */
  @Input() searchLabel = 'Search';

  /** Tooltip for search field */
  @Input() searchTooltip?: string;

  /** Name of the simple search form control */
  @Input() searchControlName = 'simpleSearch';

  /** Name of the columns form control (if using column management) */
  @Input() columnsControlName = 'columns';

  /** Whether to show the Add Filter button */
  @Input() showAddFilterButton = true;

  /** Whether to show the Columns button */
  @Input() showColumnsButton = true;

  /** Whether to show the Clear button */
  @Input() showClearButton = true;

  /** Emitted when filters change */
  @Output() filtersChanged = new EventEmitter<void>();

  /** Emitted when filters are cleared */
  @Output() filtersCleared = new EventEmitter<void>();

  @ViewChildren('filterViewRef', { read: ViewContainerRef })
  filterViewRefs: QueryList<ViewContainerRef>;

  activeFilters: Array<FilterBarFilter<TQueryKey, TFormValue>> = [];

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.columnConfig) {
      this.restoreSavedColumns();
    }
  }

  /**
   * Clear all filters except columns
   */
  clear(): void {
    // Destroy active filter components
    const filtersToRemove = [...this.activeFilters];
    filtersToRemove.forEach(filter => {
      this.removeFilterComponent(filter);
    });

    this.activeFilters = [];
    Object.keys(this.form.controls).forEach(key => {
      if (key !== this.columnsControlName) {
        this.form.controls[key].reset();
      }
    });
    this.filtersCleared.emit();
  }

  /**
   * Reset all filters including columns
   */
  reset(): void {
    this.clear();
    this.form.reset();
  }

  /**
   * Show a specific filter
   */
  showFilter(
    filter: FilterBarFilter<TQueryKey, TFormValue>,
    queries?: Array<[TQueryKey, string]>,
  ): void {
    if (!this.activeFilters.includes(filter)) {
      this.activeFilters.push(filter);
    }
    this.addFilterComponent(filter, queries);
    this.cd.detectChanges();
  }

  /**
   * Remove a specific filter
   */
  removeFilter(filter: FilterBarFilter<TQueryKey, TFormValue>): void {
    const index = this.activeFilters.indexOf(filter);
    if (index >= 0) {
      this.activeFilters.splice(index, 1);
    }
    this.removeFilterComponent(filter);
    this.cd.detectChanges();
  }

  /**
   * Check if a column should be visible
   */
  shouldShowColumn(column: TColumn): boolean {
    return this.columnConfig?.shouldShowColumn?.(column) ?? true;
  }

  /**
   * Get all active filters (non-header items)
   */
  getActiveFilters(): Array<FilterBarFilter<TQueryKey, TFormValue>> {
    return this.activeFilters;
  }

  /**
   * Get available filters (non-header items)
   */
  getAvailableNonHeaderFilters(): Array<FilterBarFilter<TQueryKey, TFormValue>> {
    return this.availableFilters.filter(
      (f): f is FilterBarFilter<TQueryKey, TFormValue> => !f.isHeader,
    );
  }

  hasAvailableFilters() {
    return (
      this.activeFilters.length === 0 ||
      this.availableFilters.some(f => !f.isHeader && !this.activeFilters.includes(f))
    );
  }

  /**
   * Restore filters from URL query parameters
   * Should be called by subclass after initialization
   */
  protected restoreFromQuery(): void {
    const query = this.route.snapshot.fragment?.split(/\?/, 2)[1];
    if (!query) {
      return;
    }

    const filtersToShow = new Map<
      FilterBarFilter<TQueryKey, TFormValue>,
      Array<[TQueryKey, string]>
    >();
    let simpleSearchValue = '';

    for (const [encodedKey, encodedValue] of query.split(/&/g).map(x => x.split(/=/, 2))) {
      const key = decodeURIComponent(encodedKey) as TQueryKey;
      const value = decodeURIComponent(encodedValue);

      if (key === (this.searchControlName as TQueryKey)) {
        simpleSearchValue = value;
        continue;
      }

      const filter = this.availableFilters.find(
        (f): f is FilterBarFilter<TQueryKey, TFormValue> => !f.isHeader && f.isMatch(key, value),
      );

      if (!filter) {
        continue;
      }

      const queries = filtersToShow.get(filter);
      if (queries) {
        queries.push([key, value]);
      } else {
        filtersToShow.set(filter, [[key, value]]);
      }
    }

    this.setSimpleSearch(simpleSearchValue);

    // Remove filters that are no longer in the query
    for (const filter of this.activeFilters) {
      if (!filtersToShow.has(filter)) {
        this.removeFilter(filter);
      }
    }

    // Add or update filters from query
    for (const [filter, queries] of filtersToShow) {
      this.showFilter(filter, queries);
    }
  }

  /**
   * Update URL with current filter state
   */
  protected updateUrl(queryParts: Array<[TQueryKey, any]>): void {
    const hashQuery = this.serializeQuery(queryParts);
    const [currentHashPath, currentHashQuery] = this.route.snapshot.fragment?.split(/\?/, 2) || [
      null,
      null,
    ];

    if (currentHashQuery !== hashQuery) {
      const newFragment = hashQuery ? `${currentHashPath ?? ''}?${hashQuery}` : currentHashPath;
      this.router.navigate([], {
        fragment: newFragment || undefined,
        replaceUrl: true,
        queryParamsHandling: 'preserve',
      });
    }
  }

  @HostListener('window:hashchange')
  onHashChange(): void {
    setTimeout(() => {
      this.restoreFromQuery();
    });
  }

  private addFilterComponent(
    filter: FilterBarFilter<TQueryKey, TFormValue>,
    queries?: Array<[TQueryKey, string]>,
  ): void {
    if (!filter.componentRef) {
      // Get form control from formControlName or componentInputs
      let formControl = filter.formControlName
        ? this.form.controls[filter.formControlName as string]
        : null;

      // If not found via formControlName, check if it's in componentInputs
      if (!formControl && filter.componentInputs?.formControl) {
        formControl = filter.componentInputs.formControl;
      }

      if (!formControl) {
        console.error(`Form control not found for filter: ${filter.label}`);
        return;
      }

      setTimeout(() => {
        const viewContainerRef = this.filterViewRefs.toArray()[this.activeFilters.indexOf(filter)];
        if (viewContainerRef) {
          const componentRef = viewContainerRef.createComponent(filter.component);
          componentRef.instance.formControl = formControl;

          // Apply any additional component inputs (excluding formControl as it's already set)
          if (filter.componentInputs) {
            Object.keys(filter.componentInputs).forEach(key => {
              if (key !== 'formControl') {
                componentRef.instance[key] = filter.componentInputs![key];
              }
            });
          }

          filter.componentRef = componentRef;

          if (queries?.length) {
            componentRef.instance.applyQueryValues(queries);
          }

          this.cd.detectChanges();
        }
      });
    } else if (queries?.length) {
      setTimeout(() => {
        filter.componentRef?.instance.applyQueryValues(queries);

        this.cd.detectChanges();
      });
    }
  }

  private removeFilterComponent(filter: FilterBarFilter<TQueryKey, TFormValue>): void {
    filter.componentRef?.destroy();
    delete filter.componentRef;

    // Reset form control if specified via formControlName
    if (filter.formControlName) {
      this.form.controls[filter.formControlName as string]?.reset();
      this.form.controls[filter.formControlName as string]?.updateValueAndValidity();
    }
    // Or reset if provided via componentInputs
    else if (filter.componentInputs?.formControl) {
      filter.componentInputs.formControl.reset();
      filter.componentInputs.formControl.updateValueAndValidity();
    }

    this.cd.detectChanges();
  }

  private restoreSavedColumns(): void {
    if (!this.columnConfig) {
      return;
    }

    const columnsJson = localStorage.getItem(this.columnConfig.storageKey);
    let activeColumns: TColumn[];

    if (columnsJson) {
      try {
        const parsedColumns = JSON.parse(columnsJson);
        if (Array.isArray(parsedColumns) && parsedColumns.length > 0) {
          activeColumns = parsedColumns;
        } else {
          activeColumns = this.columnConfig.defaultColumns;
        }
      } catch {
        activeColumns = this.columnConfig.defaultColumns;
      }
    } else {
      activeColumns = this.columnConfig.defaultColumns;
    }

    const columnsControl = this.form.controls[this.columnsControlName];
    if (columnsControl) {
      this.columnConfig.allColumns.forEach(column => {
        const columnControl = (columnsControl as FormGroup).controls[column.value.toString()];
        if (columnControl) {
          columnControl.setValue(activeColumns.includes(column.value), { emitEvent: false });
        }
      });
    }
  }

  private serializeQuery(queryParts: Array<[TQueryKey, any]>): string | null {
    return queryParts.length
      ? queryParts
          .map(([key, value]) =>
            [encodeURIComponent(key), encodeURIComponent(value.toString())].join('='),
          )
          .join('&')
      : null;
  }

  private setSimpleSearch(simpleSearch: string): void {
    if (simpleSearch && this.form.controls[this.searchControlName]) {
      this.form.controls[this.searchControlName].setValue(simpleSearch, { emitEvent: false });
    }
  }

  protected parseAndSaveActiveColumns(formColumns: any): TColumn[] {
    if (!this.columnConfig) {
      return [];
    }

    const columns: TColumn[] = [];
    Object.keys(formColumns).forEach(control => {
      if (formColumns[control]) {
        const column = this.columnConfig!.allColumns.find(c => c.value.toString() === control);
        if (column) {
          columns.push(column.value);
        }
      }
    });

    localStorage.setItem(this.columnConfig.storageKey, JSON.stringify(columns));
    return columns;
  }
}
